import { useCallback } from 'react';
import copyToClipboard from 'copy-to-clipboard';

import { Modals } from 'Services/Modals';
import { SnackBar } from 'Services/SnackBar';
import { isSystemShareSupported } from 'Shared/Share/utils';

import { trackCopyClick, trackShareClick } from '../../biEvents';
import { ArrowIcon } from '../components/ArrowIcon';
import { CopyIcon } from '../components/CopyIcon';
import { GiftIcon } from '../components/GiftIcon';
import { LinkTitle } from '../components/LinkTitle';

import type { GiftItemT } from 'Services/api/giftLink/types';

type PropsT = {
  availableGift: GiftItemT | null;
  availableGiftTotal: number;
  giftLink: string;
  onMobileShare: (url: string, addSourceParam?: boolean) => void;
};

export const GiftItem: React.FunctionComponent<PropsT> = ({
  availableGift,
  availableGiftTotal,
  giftLink,
  onMobileShare,
}) => {
  const isViewLimitReached = availableGift?.remainingViews === 0;

  const handleShare = useCallback(() => {
    trackShareClick(availableGiftTotal);
    onMobileShare(giftLink, false);
  }, [giftLink, onMobileShare, availableGiftTotal]);

  const handleCopy = useCallback(() => {
    trackCopyClick(availableGiftTotal);
    copyToClipboard(giftLink);
    Modals.removeCurrentModal();
    SnackBar.push({ content: 'Gift Link Copied' });
  }, [availableGiftTotal, giftLink]);

  const availableCountText = isViewLimitReached
    ? '(view limit reached)'
    : '(generated)';

  if (isSystemShareSupported) {
    return (
      <button
        aria-label="share-gift-link"
        className="flex w-full items-center justify-between"
        type="button"
        onClick={handleShare}
      >
        <div className="flex items-center">
          <GiftIcon />

          <LinkTitle
            availableCountText={availableCountText}
            subTitle="The recipient can read the entire article with no paywall"
            title="Share gift link"
          />
        </div>

        <ArrowIcon />
      </button>
    );
  }

  return (
    <button
      aria-label="copy-gift-link"
      className="flex w-full items-center justify-between"
      type="button"
      onClick={handleCopy}
    >
      <div className="flex items-center">
        <GiftIcon />

        <LinkTitle
          availableCountText={availableCountText}
          subTitle="The recipient can read the entire article with no paywall"
          title="Copy gift link"
        />
      </div>

      <CopyIcon />
    </button>
  );
};
